import {ChronoUnitLegacy} from "./time-utils";
import {
    ChronoUnit,
    convert,
    DateTimeFormatter,
    Instant,
    LocalDateTime,
    nativeJs,
    ZonedDateTime,
    ZoneId,
    ZoneOffset
} from "@js-joda/core";
import {InstantString} from "../api/model/general/aliases";
import {DEFAULT_DATE_FORMATTER} from "./constants";

export namespace JodaTimeUtils {
    const validHandledUnitsInFunctions = [
        ChronoUnit.MILLIS,
        ChronoUnit.SECONDS,
        ChronoUnit.MINUTES,
        ChronoUnit.HOURS,
        ChronoUnit.DAYS,
        ChronoUnit.WEEKS,
        ChronoUnit.MONTHS,
        ChronoUnit.YEARS
    ];

    export function convertLegacyChronoUnitToJoda(legacyUnit: ChronoUnitLegacy): ChronoUnit {
        const unitName = ChronoUnitLegacy[legacyUnit];
        if (!unitName) {
            throw new Error('Unsupported CHRONOS Unit Legacy value');
        }

        return ChronoUnit[unitName]
    }

    const anyDateToZonedDateTime = (date: ZonedDateTime | Date | string): ZonedDateTime => {
        if(date instanceof ZonedDateTime) {
            return ZonedDateTime.from(date)
        }

        return UTCDateToZonedDateTime(date);
    }

    const UTCDateToZonedDateTime = (date: Date | string): ZonedDateTime => {
        if (date instanceof Date) {
            return ZonedDateTime.from(nativeJs(date));
        }
        return ZonedDateTime.ofInstant(Instant.parse(date), ZoneOffset.UTC);
    }

    const LocalDateTimeToJSDate = (date: ZonedDateTime): Date => {
        return convert(date).toDate();
    }

    export const truncateTo = (date: ZonedDateTime | Date | string, unit: ChronoUnit): Date => {
        if (!validHandledUnitsInFunctions.includes(unit)) {
            throw new Error('Unhandled time unit.');
        }

        const tempDate = anyDateToZonedDateTime(date);

        const switchFunction = (unit: ChronoUnit): ZonedDateTime => {
            switch (unit) {
                case ChronoUnit.MILLIS:
                case ChronoUnit.SECONDS:
                case ChronoUnit.MINUTES:
                case ChronoUnit.HOURS:
                case ChronoUnit.DAYS:
                    return tempDate.truncatedTo(unit);
                case ChronoUnit.WEEKS:
                    return tempDate.truncatedTo(ChronoUnit.DAYS).minusDays(tempDate.dayOfWeek().value() === 0 ? 6 : tempDate.dayOfWeek().value() - 1);
                case ChronoUnit.MONTHS:
                    return tempDate.truncatedTo(ChronoUnit.DAYS).withDayOfMonth(1);
                case ChronoUnit.YEARS:
                    return tempDate.truncatedTo(ChronoUnit.DAYS).withDayOfMonth(1).withMonth(1);
                default:
                    throw new Error('Unhandled time unit.');
            }
        }

        return LocalDateTimeToJSDate(switchFunction(unit));
    }

    export const CeilingTo = (date: ZonedDateTime | Date | string, unit: ChronoUnit): Date => {
        if (!validHandledUnitsInFunctions.includes(unit)) {
            throw new Error('Unhandled time unit.');
        }

        const tempDate = anyDateToZonedDateTime(date);
        const truncatedDate = anyDateToZonedDateTime(JodaTimeUtils.truncateTo(tempDate, unit));

        const switchFunction = (unit: ChronoUnit): ZonedDateTime => {
            switch (unit) {
                case ChronoUnit.MILLIS:
                case ChronoUnit.SECONDS:
                case ChronoUnit.MINUTES:
                case ChronoUnit.HOURS:
                case ChronoUnit.DAYS:
                case ChronoUnit.WEEKS:
                case ChronoUnit.MONTHS:
                case ChronoUnit.YEARS:
                    if (truncatedDate.isBefore(tempDate)) {
                        return truncatedDate.plus(1, unit);
                    }
                    return truncatedDate;
                default:
                    throw new Error('Unhandled time unit.');
            }
        };

        return LocalDateTimeToJSDate(switchFunction(unit));
    };

    export const truncateAndOffsetStr = (date: LocalDateTime, truncateTo: string, offset: number, offsetUnit: string): LocalDateTime => {
        // @ts-ignore
        return truncateAndOffset(date, ChronoUnit[truncateTo], offset, ChronoUnit[offsetUnit])

    }
    export const truncateAndOffset = (date: LocalDateTime, truncateTo: ChronoUnit, offset: number, offsetUnit: ChronoUnit): LocalDateTime => {
        const truncatedDate = date.truncatedTo(truncateTo);
        return truncatedDate.plus(offset, offsetUnit);
    }

    export const formatInstantAsLocal = (instant: Instant | InstantString, format?: string): string => {
        const pattern = format ? DateTimeFormatter.ofPattern(format) : DEFAULT_DATE_FORMATTER;
        const instantObj: Instant = typeof instant === 'string' ? Instant.parse(instant) : instant;
        const localDateTime = instantObj.atZone(ZoneId.systemDefault()).toLocalDateTime();
        return localDateTime.format(pattern);
    }
}

